// Reconciliation page — entry point mounted at /reconciliation.
//
// Owns:
//   - period state (default = current calendar quarter)
//   - tab state (workspace | reconciled | uploads)
//   - workspace data fetch + selection state
//   - modal state (upload | budget | null)
//   - sumPolicy (persisted per-user via localStorage; default 'strict')
//   - toast / refresh-key for child views
//
// Talks to the backend through `frontend/src/reconciliation/api.js`. If a
// route 404s (backend not yet shipped), the affected view degrades to an
// empty state — no crashes.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NavHeader from '../components/NavHeader';
import {
  TOKENS,
  UI_FONT,
  MONO_FONT,
} from '../reconciliation/tokens';
import {
  Icon,
  Toast,
  styles,
} from '../reconciliation/primitives';
import WorkspaceView from '../reconciliation/WorkspaceView';
import ReconciledView from '../reconciliation/ReconciledView';
import UploadsView from '../reconciliation/UploadsView';
import UploadModal from '../reconciliation/UploadModal';
import BudgetModal from '../reconciliation/BudgetModal';
import {
  fetchWorkspace,
  fetchPeriods,
  fetchUploads,
  reconcile as apiReconcile,
  reconcileBatch as apiReconcileBatch,
  unreconcile as apiUnreconcile,
  setAside as apiSetAside,
} from '../reconciliation/api';
import '../reconciliation/Reconciliation.css';

const SUM_POLICY_KEY = 'erbi.recon.sumPolicy';

function currentQuarter() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

function readSumPolicy() {
  if (typeof window === 'undefined') return 'strict';
  try {
    const v = window.localStorage.getItem(SUM_POLICY_KEY);
    return v === 'lenient' ? 'lenient' : 'strict';
  } catch {
    return 'strict';
  }
}

export default function Reconciliation() {
  const [period, setPeriod] = useState(currentQuarter());
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [tab, setTab] = useState('workspace');
  const [workspace, setWorkspace] = useState(null);
  const [workspaceError, setWorkspaceError] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [uploadsTotal, setUploadsTotal] = useState(0);

  const [selSap, setSelSap] = useState(new Set());
  const [selInv, setSelInv] = useState(new Set());
  const [sapFilter, setSapFilter] = useState('');
  const [invFilter, setInvFilter] = useState('');

  const [modal, setModal] = useState(null); // 'upload' | 'budget' | null
  const [toast, setToast] = useState(null);

  // refreshKey forces the Reconciled / Uploads tabs to re-fetch when the
  // workspace changes underneath them.
  const [refreshKey, setRefreshKey] = useState(0);

  // Sum policy is persisted per-user (SPEC §5 "Tweaks NOT in scope") but no
  // UI toggle ships in v1. Reads from localStorage; future polish step will
  // wire a settings affordance. Default = strict.
  const [sumPolicy] = useState(readSumPolicy);

  // Period dropdown options. If the API hasn't shipped, fall back to a
  // sensible 3-period set centred on the current quarter.
  useEffect(() => {
    let cancelled = false;
    fetchPeriods()
      .then((res) => {
        if (cancelled) return;
        const arr = res.data?.periods || [];
        if (arr.length) setAvailablePeriods(arr);
        else setAvailablePeriods(defaultPeriods());
      })
      .catch(() => {
        if (!cancelled) setAvailablePeriods(defaultPeriods());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Workspace fetch — every period change + every mutation.
  const loadWorkspace = useCallback(async () => {
    setWorkspaceLoading(true);
    setWorkspaceError(null);
    try {
      const res = await fetchWorkspace(period);
      setWorkspace(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // Backend not yet ready or empty period — render empty workspace.
        setWorkspace(null);
        setWorkspaceError(null);
      } else {
        setWorkspaceError(
          err.response?.data?.detail || err.message || 'Failed to load workspace'
        );
        setWorkspace(null);
      }
    } finally {
      setWorkspaceLoading(false);
    }
  }, [period]);

  // Uploads count for the tab badge — the workspace endpoint doesn't
  // surface it, so we fetch a single row to read `total`.
  useEffect(() => {
    let cancelled = false;
    fetchUploads(period, { limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setUploadsTotal(data.total ?? (data.uploads || data.imports || []).length);
      })
      .catch(() => {
        if (!cancelled) setUploadsTotal(0);
      });
    return () => {
      cancelled = true;
    };
  }, [period, refreshKey]);

  useEffect(() => {
    loadWorkspace();
    setSelSap(new Set());
    setSelInv(new Set());
  }, [loadWorkspace]);

  // Mutation handlers — passed down to WorkspaceView. Return true on success
  // so the child can clear selection.
  const handleReconcile = async (sapIds, invoiceIds) => {
    try {
      await apiReconcile(sapIds, invoiceIds, { period, policy: sumPolicy });
      setToast({
        text: `Reconciled ${sapIds.length} SAP row${sapIds.length === 1 ? '' : 's'} ↔ ${invoiceIds.length} invoice${
          invoiceIds.length === 1 ? '' : 's'
        }`,
        tone: 'good',
      });
      await loadWorkspace();
      setRefreshKey((k) => k + 1);
      return true;
    } catch (err) {
      const data = err.response?.data;
      let msg = 'Reconcile failed';
      if (data?.code === 'sum_mismatch') {
        msg = `Sum mismatch: SAP ${data.sap_total}, INV ${data.invoice_total} (Δ ${data.delta})`;
      } else if (data?.code === 'sap_already_matched') {
        msg = 'Some SAP rows are already reconciled — refresh the workspace';
      } else if (data?.code === 'invoice_already_matched') {
        msg = 'Some invoices are already reconciled — refresh the workspace';
      } else if (data?.detail) {
        msg = data.detail;
      } else if (err.message) {
        msg = err.message;
      }
      setToast({ text: msg, tone: 'miss' });
      return false;
    }
  };

  // Reconcile a whole VAT/Lux batch (matched on its payment total).
  const handleReconcileBatch = async (sapIds, batchKind, batchId) => {
    try {
      await apiReconcileBatch(sapIds, batchKind, batchId, { period, policy: sumPolicy });
      setToast({
        text: `Reconciled ${batchKind.toUpperCase()} batch`,
        tone: 'good',
      });
      await loadWorkspace();
      setRefreshKey((k) => k + 1);
      return true;
    } catch (err) {
      const data = err.response?.data;
      let msg = 'Batch reconcile failed';
      if (data?.code === 'sum_mismatch') {
        msg = `Sum mismatch: SAP ${data.sap_total}, batch ${data.invoice_total} (Δ ${data.delta})`;
      } else if (data?.detail) {
        msg = typeof data.detail === 'string' ? data.detail : msg;
      } else if (err.message) {
        msg = err.message;
      }
      setToast({ text: msg, tone: 'miss' });
      return false;
    }
  };

  const handleSetAside = async (sapIds, reason) => {
    try {
      await apiSetAside(sapIds, reason);
      setToast({
        text: `Set aside ${sapIds.length} row${sapIds.length === 1 ? '' : 's'}`,
        tone: 'good',
      });
      await loadWorkspace();
      setRefreshKey((k) => k + 1);
      return true;
    } catch (err) {
      setToast({
        text: err.response?.data?.detail || err.message || 'Set aside failed',
        tone: 'miss',
      });
      return false;
    }
  };

  const handleUnreconcile = async (groupId) => {
    try {
      await apiUnreconcile(groupId);
      setToast({ text: 'Unreconciled — back in workspace', tone: 'good' });
      await loadWorkspace();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setToast({
        text: err.response?.data?.detail || err.message || 'Unreconcile failed',
        tone: 'miss',
      });
    }
  };

  // Stat strip data — derived from the workspace projection. Fallback zeros
  // when the projection is empty (backend not ready or no data).
  const stats = useMemo(() => {
    const s = workspace?.stats || {};
    return {
      sap_rows: s.sap_rows ?? 0,
      invoices: s.invoices ?? 0,
      reconciled: s.reconciled ?? 0,
      reconciled_vendor_count: s.reconciled_vendor_count ?? 0,
      reconciled_usd: Number(s.reconciled_usd ?? 0),
      sap_unmatched: s.sap_unmatched ?? (workspace?.sap?.length ?? 0),
      sap_unmatched_usd: Number(s.sap_unmatched_usd ?? 0),
      inv_unmatched: s.inv_unmatched ?? (workspace?.invoices?.length ?? 0),
      inv_unmatched_usd: Number(s.inv_unmatched_usd ?? 0),
      auto_matched_today: s.auto_matched_today ?? 0,
      last_upload_at: s.last_upload_at,
      budget_actual_usd: s.budget_actual_usd ?? 0,
    };
  }, [workspace]);

  return (
    <>
      <NavHeader />
      <div className="recon-page">
        {/* Header bar */}
        <ReconHeader
          period={period}
          setPeriod={setPeriod}
          availablePeriods={availablePeriods}
          stats={stats}
          onOpenBudget={() => setModal('budget')}
          onUpload={() => setModal('upload')}
        />

        {/* Tabs row */}
        <TabsRow
          tab={tab}
          onTab={setTab}
          counts={{
            unmatched: stats.sap_unmatched + stats.inv_unmatched,
            reconciled: stats.reconciled,
            uploads: uploadsTotal,
          }}
        />

        <div className="recon-scroll">
          {tab === 'workspace' && (
            <>
              {/* Only show the full-screen loading/error states on the INITIAL
                  load. Once we have workspace data, keep WorkspaceView mounted
                  through background refreshes (post-reconcile, SSE) — otherwise
                  it unmounts on every mutation, which (among other things)
                  would slam the "probable matches" modal shut after each
                  one-click reconcile. */}
              {workspaceLoading && !workspace ? (
                <div className="recon-empty">
                  <div className="title">Loading workspace…</div>
                  <div className="sub">Fetching SAP rows and invoices for {period}.</div>
                </div>
              ) : workspaceError && !workspace ? (
                <div className="recon-empty">
                  <div className="title">Couldn't load workspace</div>
                  <div className="sub">{workspaceError}</div>
                </div>
              ) : (
                <WorkspaceView
                  sap={workspace?.sap || []}
                  invoices={workspace?.invoices || []}
                  selSap={selSap}
                  setSelSap={setSelSap}
                  selInv={selInv}
                  setSelInv={setSelInv}
                  sapFilter={sapFilter}
                  setSapFilter={setSapFilter}
                  invFilter={invFilter}
                  setInvFilter={setInvFilter}
                  onReconcile={handleReconcile}
                  onReconcileBatch={handleReconcileBatch}
                  onSetAside={handleSetAside}
                  onToast={setToast}
                  sumPolicy={sumPolicy}
                />
              )}
            </>
          )}

          {tab === 'reconciled' && (
            <ReconciledView
              period={period}
              onUnreconcile={handleUnreconcile}
              refreshKey={refreshKey}
            />
          )}

          {tab === 'uploads' && <UploadsView period={period} refreshKey={refreshKey} />}
        </div>
      </div>

      {modal === 'upload' && (
        <UploadModal
          period={period}
          onClose={() => setModal(null)}
          onApplied={({ importQuarter } = {}) => {
            // Switch the page period to the quarter the import landed
            // in — otherwise the user uploads a Q1 file while the page
            // is on Q2 (today's quarter), clicks Apply, and sees an
            // empty workspace. Also refresh the period dropdown so the
            // new quarter is selectable. SSE wiring is Phase D scope.
            if (importQuarter && importQuarter !== period) {
              setPeriod(importQuarter);
              setAvailablePeriods((prev) =>
                prev.includes(importQuarter) ? prev : [importQuarter, ...prev]
              );
            } else {
              loadWorkspace();
            }
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {modal === 'budget' && <BudgetModal period={period} onClose={() => setModal(null)} />}

      {toast && (
        <Toast text={toast.text} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}

// ─── Page header (period strip + Budget Actual + Upload + stat strip) ──
function ReconHeader({
  period,
  setPeriod,
  availablePeriods,
  stats,
  onOpenBudget,
  onUpload,
}) {
  const budgetUSD = Number(stats.budget_actual_usd || 0);
  const budgetLabel =
    Math.abs(budgetUSD) >= 1_000_000
      ? `$${(budgetUSD / 1_000_000).toFixed(2)}M`
      : `$${budgetUSD.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  return (
    <div
      style={{
        padding: '14px 18px 12px',
        background: TOKENS.panel,
        borderBottom: `1px solid ${TOKENS.border}`,
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              fontFamily: UI_FONT,
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.015em',
            }}
          >
            <span>Reconciliation ·</span>
            <PeriodSelect period={period} setPeriod={setPeriod} options={availablePeriods} />
          </div>
          <div
            style={{
              fontFamily: UI_FONT,
              fontSize: 11.5,
              color: TOKENS.ink3,
              marginTop: 2,
            }}
          >
            Match SAP rows against invoices sent to AP. Anything that doesn't match stays in the queue.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={onOpenBudget} style={styles.ghostBtn}>
            <Icon d="M3 3v18h18 M7 14l4-4 4 4 5-5" size={12} sw={2} />
            Actual Spend (per SAP) · {budgetLabel}
          </button>
          <button onClick={onUpload} style={styles.primaryBtnLg}>
            <Icon d="M12 16V4m0 0l-5 5m5-5l5 5M4 20h16" size={13} sw={2} />
            Upload SAP pull
          </button>
        </div>
      </div>

      {/* 3 amount cards — all USD, all vendor-side, so they're comparable. */}
      <div style={{ display: 'flex', gap: 10 }}>
        <StatCard
          label="Reconciled Invoices"
          amount={stats.reconciled_usd}
          sub={`${stats.reconciled_vendor_count.toLocaleString()} match${stats.reconciled_vendor_count === 1 ? '' : 'es'}`}
          tone="ok"
        />
        <StatCard
          label="Unreconciled · SAP"
          amount={stats.sap_unmatched_usd}
          sub={`${stats.sap_unmatched.toLocaleString()} row${stats.sap_unmatched === 1 ? '' : 's'} ingested, not matched`}
          tone={stats.sap_unmatched ? 'warn' : 'ok'}
        />
        <StatCard
          label="Unreconciled · Invoices"
          amount={stats.inv_unmatched_usd}
          sub={`${stats.inv_unmatched.toLocaleString()} sent to AP, not matched`}
          tone={stats.inv_unmatched ? 'warn' : 'ok'}
        />
      </div>
    </div>
  );
}

// Summary card: USD amount headline + count sub-label. Three of these
// replace the old count-pill strip — fewer, larger, self-explanatory.
function StatCard({ label, amount, sub, tone }) {
  const accent =
    tone === 'warn' ? TOKENS.warn : tone === 'ok' ? TOKENS.ok : TOKENS.ink3;
  const n = Number(amount || 0);
  const display =
    (n < 0 ? '−' : '') +
    '$' +
    Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: '10px 14px',
        borderRadius: 9,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
        borderLeft: `3px solid ${accent}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <span
        style={{
          fontFamily: MONO_FONT,
          fontSize: 9.5,
          color: TOKENS.ink3,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: MONO_FONT,
          fontSize: 24,
          fontWeight: 500,
          color: tone === 'warn' ? TOKENS.warn : TOKENS.ink,
          fontFeatureSettings: '"tnum"',
          letterSpacing: '-0.015em',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </span>
      <span style={{ fontFamily: UI_FONT, fontSize: 11, color: TOKENS.ink3 }}>
        {sub}
      </span>
    </div>
  );
}

function PeriodSelect({ period, setPeriod, options }) {
  return (
    <select
      value={period}
      onChange={(e) => setPeriod(e.target.value)}
      style={{
        fontFamily: MONO_FONT,
        fontSize: 14,
        fontWeight: 600,
        color: TOKENS.ink,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 6,
        padding: '3px 8px',
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      {(options.includes(period) ? options : [period, ...options]).map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
  );
}

function TabsRow({ tab, onTab, counts }) {
  const items = [
    {
      id: 'workspace',
      label: 'To reconcile',
      count: counts.unmatched,
      urgent: counts.unmatched > 0,
    },
    { id: 'reconciled', label: 'Reconciled', count: counts.reconciled },
    { id: 'uploads', label: 'Uploads', count: counts.uploads },
  ];
  return (
    <div
      style={{
        padding: '0 18px',
        background: TOKENS.panel,
        borderBottom: `1px solid ${TOKENS.border}`,
        flex: '0 0 auto',
        display: 'flex',
        gap: 0,
      }}
    >
      {items.map((it) => {
        const active = it.id === tab;
        return (
          <button
            key={it.id}
            onClick={() => onTab(it.id)}
            style={{
              position: 'relative',
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              fontFamily: UI_FONT,
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              color: active ? TOKENS.ink : TOKENS.ink2,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {it.label}
            <span
              style={{
                fontFamily: MONO_FONT,
                fontSize: 10.5,
                padding: '1px 7px',
                borderRadius: 9,
                background:
                  active && it.urgent
                    ? 'oklch(0.42 0.16 70)'
                    : active
                    ? TOKENS.ink
                    : TOKENS.panelAlt,
                color: active && it.urgent ? '#fff' : active ? '#fff' : TOKENS.ink3,
                border: !active ? `1px solid ${TOKENS.border}` : 'none',
                fontFeatureSettings: '"tnum"',
              }}
            >
              {(it.count || 0).toLocaleString()}
            </span>
            {active && (
              <span
                style={{
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: -1,
                  height: 2,
                  background: TOKENS.ink,
                  borderRadius: 1,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function defaultPeriods() {
  // Current + previous 4 quarters.
  const d = new Date();
  const year = d.getFullYear();
  const q = Math.floor(d.getMonth() / 3) + 1;
  const out = [];
  for (let i = 0; i < 5; i += 1) {
    let yy = year;
    let qq = q - i;
    while (qq <= 0) {
      qq += 4;
      yy -= 1;
    }
    out.push(`${yy}-Q${qq}`);
  }
  return out;
}

